/**
 * Minimal JMAP client for Stalwart (RFC 8620 core, RFC 8621 mail).
 *
 * Same-origin by design: Caddy routes /jmap and /.well-known/jmap on
 * mail.syndeo.cloud straight to Stalwart, so the browser never makes a
 * cross-origin request and no CORS configuration is required.
 *
 * Verified against Stalwart v0.15.5, which advertises:
 *   urn:ietf:params:jmap:core, :mail, :calendars
 */

import { parseSearch } from "./search";
import { textToHtml } from "./html-to-text";

export const CORE = "urn:ietf:params:jmap:core";
export const MAIL = "urn:ietf:params:jmap:mail";
export const SUBMISSION = "urn:ietf:params:jmap:submission";
export const VACATION = "urn:ietf:params:jmap:vacationresponse";

/** The automatic reply (RFC 8621 §8). A singleton per account. */
export interface VacationResponse {
  id: string;
  isEnabled: boolean;
  /** UTCDate, or null for "until I turn it off". */
  fromDate: string | null;
  toDate: string | null;
  subject: string | null;
  textBody: string | null;
  htmlBody: string | null;
}

export interface JmapSession {
  apiUrl: string;
  downloadUrl: string;
  uploadUrl: string;
  primaryAccounts: Record<string, string>;
  accounts: Record<string, { name: string }>;
  capabilities: Record<string, unknown>;
}

export interface Mailbox {
  id: string;
  name: string;
  role: string | null;
  parentId: string | null;
  totalEmails: number;
  unreadEmails: number;
  sortOrder: number;
}

export interface EmailAddress {
  name: string | null;
  email: string;
}

/** A part of a message the recipient is meant to keep (RFC 8621 EmailBodyPart). */
export interface Attachment {
  partId: string | null;
  blobId: string;
  size: number;
  name: string | null;
  type: string;
  disposition: string | null;
  cid: string | null;
}

/** The result of putting bytes on the server, before they belong to a message. */
export interface UploadedBlob {
  accountId: string;
  blobId: string;
  type: string;
  size: number;
}

/** A message being written — the same shape whether it is saved or sent. */
export interface DraftMessage {
  to: EmailAddress[];
  cc?: EmailAddress[];
  bcc?: EmailAddress[];
  subject: string;
  /** Always present — the alternative every non-HTML consumer reads. */
  text: string;
  /** Present when the message was composed with formatting. */
  html?: string | null;
  /**
   * Whether the identity signature is appended. False for replies when
   * "Signature on replies" is off — Outlook has the same switch, and people who
   * use it do not want their block repeated down a long thread.
   */
  includeSignature?: boolean;
  inReplyTo?: string[] | null;
  references?: string[] | null;
  attachments?: {
    blobId: string;
    type: string;
    name: string;
    /** Set on parts the HTML references inline; see lib/inline-images.ts. */
    cid?: string;
    disposition?: string;
  }[];
}

/** A sending identity (RFC 8621 §6) — required to submit a message. */
export interface Identity {
  id: string;
  name: string;
  email: string;
  replyTo: EmailAddress[] | null;
  bcc: EmailAddress[] | null;
  textSignature: string;
  htmlSignature: string;
}

export interface Email {
  id: string;
  threadId: string;
  mailboxIds: Record<string, boolean>;
  keywords: Record<string, boolean>;
  from: EmailAddress[] | null;
  to: EmailAddress[] | null;
  cc?: EmailAddress[] | null;
  subject: string | null;
  receivedAt: string;
  preview: string;
  hasAttachment: boolean;
  messageId?: string[] | null;
  inReplyTo?: string[] | null;
  references?: string[] | null;
  attachments?: Attachment[] | null;
  bodyValues?: Record<string, { value: string; isTruncated: boolean }>;
  textBody?: { partId: string; type: string }[];
  /** May contain the TEXT part when the message has no HTML — see bodyOf(). */
  htmlBody?: { partId: string; type: string }[];
}

/** A JMAP-level failure. `type` is the server's error type where present. */
export class JmapError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly type?: string,
  ) {
    super(message);
    this.name = "JmapError";
  }
}

type MethodCall = [string, Record<string, unknown>, string];

/** A per-record rejection from a `/set` call. */
interface SetError {
  type?: string;
  description?: string;
  properties?: string[];
}

/**
 * A `/set` method that rejects a record still returns HTTP 200 with a
 * *successful* method response — the rejection lives in `notCreated` /
 * `notUpdated` / `notDestroyed`. Only looking for a top-level `error` therefore
 * reports a failed send as a success, which is exactly how a send that Stalwart
 * rejected for a missing `identityId` left the message sitting in Drafts while
 * the composer closed as though it had gone out.
 */
function assertSetSucceeded(payload: unknown): void {
  if (!payload || typeof payload !== "object") return;
  for (const key of ["notCreated", "notUpdated", "notDestroyed"] as const) {
    const rejected = (payload as Record<string, unknown>)[key] as
      | Record<string, SetError>
      | null
      | undefined;
    const first = rejected && Object.values(rejected)[0];
    if (first) {
      throw new JmapError(first.description ?? `The mail server rejected the change (${key})`, undefined, first.type);
    }
  }
}

/** Raised when the session cannot be recovered and the user must sign in again. */
export class JmapAuthError extends JmapError {
  constructor(message = "Your session expired. Please sign in again.") {
    super(message, 401, "unauthorized");
    this.name = "JmapAuthError";
  }
}

/** Nothing on this server should take this long; past it, something is wrong. */
const REQUEST_TIMEOUT_MS = 30_000;

export class JmapClient {
  private session: JmapSession | null = null;
  private renewing: Promise<string> | null = null;

  /**
   * @param renew Exchanges the refresh token for a new access token. Called at
   *              most once per 401; if it fails the caller gets a JmapAuthError.
   */
  constructor(
    private token: string,
    private renew?: () => Promise<string>,
  ) {}

  private headers(): HeadersInit {
    return {
      authorization: `Bearer ${this.token}`,
      "content-type": "application/json",
    };
  }

  /**
   * Every request to the mail server goes through here.
   *
   * Three things it enforces that a bare fetch() does not:
   *
   * A timeout. Without one a stalled request is a permanent spinner: the folder
   * list sits on "Loading…" forever with nothing to click and nothing logged.
   * That is the failure this client actually exhibited during testing, and an
   * error someone can see and retry is strictly better than a spinner.
   *
   * `credentials: "omit"`. Authentication is the Bearer token. Stalwart also
   * sets its own session cookie during the OAuth exchange, which the browser
   * would otherwise attach to every call — an ambient credential nobody asked
   * for, riding on requests that already carry an explicit one.
   *
   * Token renewal. Access tokens last about an hour. Before this, the app
   * simply stopped working mid-session; now a 401 triggers one refresh (shared,
   * so parallel calls do not each renew) and the request is retried, and if
   * renewal fails the caller gets a JmapAuthError to send the user to sign-in.
   */
  private async http(path: string, init: RequestInit = {}, retrying = false): Promise<Response> {
    const res = await fetch(path, {
      ...init,
      credentials: "omit",
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    if (res.status !== 401 || retrying) return res;

    // One attempt to renew, shared so parallel calls do not each refresh.
    if (!this.renew) throw new JmapAuthError();
    try {
      this.renewing ??= this.renew();
      this.token = await this.renewing;
    } catch {
      throw new JmapAuthError();
    } finally {
      this.renewing = null;
    }

    this.session = null; // fetched with the old token
    const headers = { ...(init.headers as Record<string, string>), authorization: `Bearer ${this.token}` };
    return this.http(path, { ...init, headers }, true);
  }

  /** Fetches and caches the session object; also yields the primary account id. */
  async connect(): Promise<JmapSession> {
    if (this.session) return this.session;
    const res = await this.http("/.well-known/jmap", {
      headers: this.headers(),
      redirect: "follow",
    });
    if (!res.ok) {
      if (res.status === 401) throw new JmapAuthError();
      throw new JmapError(`Could not reach the mail server (${res.status})`, res.status);
    }
    this.session = (await res.json()) as JmapSession;
    return this.session;
  }

  /**
   * The primary mail account id.
   *
   * MUST be awaited before building a method-call array: the array literal is
   * evaluated before request() gets a chance to run connect(), so reading a
   * synchronous getter there threw "No mail account on this session".
   */
  async account(): Promise<string> {
    const session = await this.connect();
    const id = session.primaryAccounts?.[MAIL];
    if (!id) throw new JmapError("No mail account on this session");
    return id;
  }

  /**
   * Issues one JMAP request. Method calls are batched in a single round trip —
   * that is the whole point of JMAP, and it is why the inbox loads in one
   * request instead of one-per-mailbox.
   */
  async request<T = unknown>(calls: MethodCall[], using: string[] = [CORE, MAIL]): Promise<T[]> {
    await this.connect();
    // Deliberately NOT session.apiUrl: Stalwart reports its INTERNAL address
    // there (http://mail.syndeo.cloud:8080/jmap/), which the browser cannot
    // reach and which would break same-origin. Caddy routes /jmap to Stalwart.
    const res = await this.http("/jmap/", {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({ using, methodCalls: calls }),
    });
    if (!res.ok) {
      if (res.status === 401) throw new JmapAuthError();
      throw new JmapError(`Mail server error (${res.status})`, res.status);
    }
    const body = (await res.json()) as { methodResponses: [string, unknown, string][] };
    // Walk in call order and report the FIRST failure of either shape. Scanning
    // for `error` responses before per-record rejections surfaced the cascade
    // instead of the cause: a rejected Email/set makes the EmailSubmission that
    // back-references it fail with "Invalid reference to non-existing object
    // draft", which says nothing about why the draft was rejected.
    for (const [name, payload] of body.methodResponses) {
      if (name === "error") {
        const detail = payload as { type?: string; description?: string };
        throw new JmapError(detail.description ?? "Request rejected", undefined, detail.type);
      }
      assertSetSucceeded(payload);
    }
    return body.methodResponses.map(([, payload]) => payload) as T[];
  }

  /**
   * Stalwart returns every mailbox with `sortOrder: 0`, so sorting on it leaves
   * the order arbitrary. Order by role to match the design's folder list, with
   * any user-created folders after the well-known ones, alphabetically.
   */
  private static ROLE_ORDER = ["inbox", "drafts", "sent", "archive", "junk", "trash"];

  async mailboxes(): Promise<Mailbox[]> {
    const accountId = await this.account();
    const [result] = await this.request<{ list: Mailbox[] }>([
      ["Mailbox/get", { accountId: accountId, ids: null }, "0"],
    ]);
    const rank = (m: Mailbox) => {
      const i = JmapClient.ROLE_ORDER.indexOf(m.role ?? "");
      return i === -1 ? JmapClient.ROLE_ORDER.length : i;
    };
    return [...result.list].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }

  /**
   * The sending identities on this account.
   *
   * RFC 8621 makes `identityId` mandatory on EmailSubmission and Stalwart
   * enforces it, so a send cannot be built from the mailbox address alone.
   * Cached: identities change about as often as the account does.
   */
  private identityCache: Identity[] | null = null;

  async identities(): Promise<Identity[]> {
    if (this.identityCache) return this.identityCache;
    const accountId = await this.account();
    const [result] = await this.request<{ list: Identity[] }>(
      [["Identity/get", { accountId, ids: null }, "0"]],
      [CORE, SUBMISSION],
    );
    this.identityCache = result.list;
    return result.list;
  }

  /** The identity that sends as `email`, falling back to the account default. */
  async identityFor(email: string): Promise<Identity> {
    const list = await this.identities();
    const match = list.find((i) => i.email.toLowerCase() === email.toLowerCase()) ?? list[0];
    if (!match) throw new JmapError("This mailbox has no sending identity");
    return match;
  }

  /**
   * Updates a sending identity — the display name and signature that go out on
   * every message from this mailbox.
   */
  async updateIdentity(id: string, patch: { name?: string; textSignature?: string }): Promise<void> {
    const accountId = await this.account();
    await this.request(
      [["Identity/set", { accountId, update: { [id]: patch } }, "0"]],
      [CORE, SUBMISSION],
    );
    this.identityCache = null; // the cached copy is now stale
  }

  /**
   * The automatic reply — Outlook's "Automatic replies (Out of office)".
   *
   * A property of the mailbox, not of this browser: it has to keep answering
   * while the tab is closed, so it lives on the server as a JMAP
   * VacationResponse singleton rather than in localStorage with the rest of the
   * preferences.
   */
  async vacation(): Promise<VacationResponse | null> {
    const accountId = await this.account();
    const [result] = await this.request<{ list: VacationResponse[] }>(
      [["VacationResponse/get", { accountId, ids: null }, "0"]],
      [CORE, VACATION],
    );
    return result.list[0] ?? null;
  }

  async setVacation(patch: Partial<VacationResponse>): Promise<void> {
    const accountId = await this.account();
    await this.request(
      // "singleton" is the only id this type has, per RFC 8621 §8.
      [["VacationResponse/set", { accountId, update: { singleton: patch } }, "0"]],
      [CORE, VACATION],
    );
  }

  /** Creates a mailbox and returns its id. Used for Archive, which Stalwart does not ship. */
  async createMailbox(name: string, role?: string): Promise<string> {
    const accountId = await this.account();
    const [result] = await this.request<{ created?: Record<string, { id: string }> }>([
      [
        "Mailbox/set",
        { accountId: accountId, create: { box: { name, parentId: null, role: role ?? null } } },
        "0",
      ],
    ]);
    const id = result.created?.box?.id;
    if (!id) throw new JmapError(`Could not create the ${name} folder`);
    return id;
  }

  /**
   * Lists a mailbox. Uses back-references (`#ids`) so query + fetch happen in
   * one round trip rather than two.
   */
  async emails(mailboxId: string, limit = 50, search?: string): Promise<Email[]> {
    const accountId = await this.account();
    // Operators become their own conditions ANDed with the mailbox, rather than
    // one blunt `text` match — so `from:helena is:unread` narrows twice.
    const extra = search ? parseSearch(search) : [];
    const filter: Record<string, unknown> = extra.length
      ? { operator: "AND", conditions: [{ inMailbox: mailboxId }, ...extra] }
      : { inMailbox: mailboxId };

    const responses = await this.request<unknown>([
      [
        "Email/query",
        {
          accountId: accountId,
          filter,
          sort: [{ property: "receivedAt", isAscending: false }],
          limit,
        },
        "0",
      ],
      [
        "Email/get",
        {
          accountId: accountId,
          "#ids": { resultOf: "0", name: "Email/query", path: "/ids" },
          properties: [
            "id", "threadId", "mailboxIds", "keywords", "from", "to", "cc",
            "subject", "receivedAt", "preview", "hasAttachment",
            // Threading headers belong in the LIST fetch, not just the full
            // message fetch: replying with `r` from the message list never
            // opens the message, so without these the reply goes out with no
            // In-Reply-To and lands in the recipient's client as a brand new
            // conversation. It did, until a real reply was sent and the
            // headers inspected.
            "messageId", "references",
          ],
        },
        "1",
      ],
    ]);
    return (responses[1] as { list: Email[] }).list;
  }

  /**
   * Fetches messages with their bodies.
   *
   * Takes the whole set in one request: opening a conversation needs every
   * message in it, and asking one at a time would be a round trip per reply.
   */
  async emailsWithBodies(ids: string[]): Promise<Email[]> {
    if (ids.length === 0) return [];
    const accountId = await this.account();
    const [result] = await this.request<{ list: Email[] }>([
      [
        "Email/get",
        {
          accountId,
          ids,
          properties: [
            "id", "threadId", "mailboxIds", "keywords", "from", "to", "cc",
            "subject", "receivedAt", "preview", "hasAttachment",
            "messageId", "inReplyTo", "references", "attachments",
            "textBody", "htmlBody", "bodyValues",
          ],
          fetchHTMLBodyValues: true,
          fetchTextBodyValues: true,
          maxBodyValueBytes: 700_000,
        },
        "0",
      ],
    ]);
    return result.list;
  }

  /** Fetches one message with its body, preferring HTML then plain text. */
  async email(id: string): Promise<Email> {
    const accountId = await this.account();
    const [result] = await this.request<{ list: Email[] }>([
      [
        "Email/get",
        {
          accountId: accountId,
          ids: [id],
          properties: [
            "id", "threadId", "mailboxIds", "keywords", "from", "to", "cc",
            "subject", "receivedAt", "preview", "hasAttachment",
            "messageId", "inReplyTo", "references", "attachments",
            "textBody", "htmlBody", "bodyValues",
          ],
          fetchHTMLBodyValues: true,
          fetchTextBodyValues: true,
          maxBodyValueBytes: 700_000,
        },
        "0",
      ],
    ]);
    const email = result.list[0];
    if (!email) throw new JmapError("Message not found");
    return email;
  }

  /** Adds or removes a keyword ($seen, $flagged, …) on a message. */
  async setKeyword(id: string, keyword: string, on: boolean): Promise<void> {
    return this.setKeywords([id], keyword, on);
  }

  /**
   * The Email object for a draft, shared by save and send so the message that
   * goes out is byte-for-byte the one that was last saved.
   */
  private draftObject(
    draft: DraftMessage,
    draftsId: string,
    identity: Identity,
  ): Record<string, unknown> {
    const signature = draft.includeSignature === false ? "" : identity.textSignature;
    const text = signature ? `${draft.text}\n\n-- \n${signature}` : draft.text;
    const html = draft.html
      ? (signature
          // The standard "-- " separator, so other clients can fold it away.
          ? `${draft.html}<div><br></div><div>-- </div>${textToHtml(signature)}`
          : draft.html)
      : null;

    return {
      mailboxIds: { [draftsId]: true },
      keywords: { $draft: true },
      from: [{ name: identity.name || null, email: identity.email }],
      to: draft.to,
      // Absent, never null. On a create, null is not "no value" — it is the
      // update-time removal semantic, and Stalwart rejects the whole record
      // with invalidProperties: ["cc"]. Same for bcc and attachments.
      ...(draft.cc?.length ? { cc: draft.cc } : {}),
      ...(draft.bcc?.length ? { bcc: draft.bcc } : {}),
      ...(identity.replyTo ? { replyTo: identity.replyTo } : {}),
      ...(draft.inReplyTo?.length ? { inReplyTo: draft.inReplyTo } : {}),
      ...(draft.references?.length ? { references: draft.references } : {}),
      subject: draft.subject,
      // textBody/htmlBody + attachments, NOT bodyStructure: RFC 8621 treats
      // them as two alternative ways to describe the same MIME tree and
      // Stalwart rejects a create that sets both ("Cannot set both properties
      // on a same request"). The convenience form lets the server assemble the
      // multipart/alternative.
      //
      // Both parts always go out when the message is rich. A recipient whose
      // client refuses HTML, a notification preview, and this app's own message
      // list all read the text part — send HTML alone and the message looks
      // empty in every one of them.
      textBody: [{ partId: "text", type: "text/plain" }],
      ...(html ? { htmlBody: [{ partId: "html", type: "text/html" }] } : {}),
      bodyValues: {
        text: { value: text },
        ...(html ? { html: { value: html } } : {}),
      },
      ...(draft.attachments?.length
        ? {
            attachments: draft.attachments.map((a) => ({
              blobId: a.blobId,
              type: a.type,
              name: a.name,
              // An inline part carries its Content-ID and says so, which is
              // what makes the recipient's client render it in the body rather
              // than list it in the attachment tray.
              ...(a.cid ? { cid: a.cid } : {}),
              disposition: a.disposition ?? "attachment",
            })),
          }
        : {}),
    };
  }

  /**
   * Writes a draft to the Drafts folder and returns its id.
   *
   * Replaces rather than updates: a JMAP Email is immutable apart from its
   * mailboxes and keywords, so there is no way to edit a saved draft's body in
   * place. Create the new revision, then destroy the old one — in that order,
   * so a failure between the two leaves the previous draft rather than nothing.
   */
  async saveDraft(
    draft: DraftMessage,
    draftsId: string,
    identity: Identity,
    replacing?: string | null,
  ): Promise<string> {
    const accountId = await this.account();
    const [result] = await this.request<{ created?: Record<string, { id: string }> }>([
      [
        "Email/set",
        { accountId, create: { draft: this.draftObject(draft, draftsId, identity) } },
        "0",
      ],
    ]);
    const id = result.created?.draft?.id;
    if (!id) throw new JmapError("Could not save the draft");
    if (replacing && replacing !== id) {
      await this.destroy([replacing]).catch(() => {
        /* the new revision is saved; a stale copy is better than losing both */
      });
    }
    return id;
  }

  /**
   * Sends a message.
   *
   * JMAP has no "send" verb: you create the Email in Drafts and then create an
   * EmailSubmission referencing it. Both happen in one request using a
   * back-reference (#draft), and `onSuccessUpdateEmail` moves the message to
   * Sent and clears $draft once the server accepts it — so a failed send leaves
   * the draft intact rather than losing the message.
   *
   * `replacing` is the id of an autosaved draft; it is destroyed only after the
   * submission succeeds, so an interrupted send still leaves a recoverable copy
   * in Drafts.
   *
   * The envelope's rcptTo carries cc AND bcc: bcc recipients must reach the
   * SMTP envelope while staying out of the header, which is why they are added
   * here and not in the Email object.
   */
  async send(
    draft: DraftMessage,
    mailboxes: { draftsId: string; sentId: string },
    identity: Identity,
    replacing?: string | null,
  ): Promise<void> {
    const accountId = await this.account();
    const envelopeTo = [...draft.to, ...(draft.cc ?? []), ...(draft.bcc ?? [])];
    if (envelopeTo.length === 0) throw new JmapError("Add at least one recipient");

    await this.request(
      [
        [
          "Email/set",
          {
            accountId,
            create: { draft: this.draftObject(draft, mailboxes.draftsId, identity) },
          },
          "0",
        ],
        [
          "EmailSubmission/set",
          {
            accountId,
            create: {
              send: {
                // identityId is mandatory — Stalwart rejects the submission
                // without it (invalidProperties), and before assertSetSucceeded
                // existed that rejection was silently reported as a send.
                identityId: identity.id,
                emailId: "#draft",
                envelope: {
                  mailFrom: { email: identity.email },
                  rcptTo: envelopeTo.map((r) => ({ email: r.email })),
                },
              },
            },
            onSuccessUpdateEmail: {
              "#send": {
                [`mailboxIds/${mailboxes.draftsId}`]: null,
                [`mailboxIds/${mailboxes.sentId}`]: true,
                "keywords/$draft": null,
                "keywords/$seen": true,
              },
            },
          },
          "1",
        ],
      ],
      [CORE, MAIL, SUBMISSION],
    );

    // Only now: the autosaved copy stays until the message has actually gone.
    if (replacing) {
      await this.destroy([replacing]).catch(() => {
        /* sent successfully; a leftover draft is a tidiness problem, not a loss */
      });
    }
  }

  /** The server's ceiling on a single upload, in bytes. */
  async maxUploadBytes(): Promise<number> {
    const session = await this.connect();
    const core = session.capabilities[CORE] as { maxSizeUpload?: number } | undefined;
    return core?.maxSizeUpload ?? 25 * 1024 * 1024;
  }

  /**
   * Puts a file on the server and returns the blob it became.
   *
   * Same-origin path built here rather than from `session.uploadUrl`, for the
   * same reason as apiUrl: Stalwart reports its internal address there.
   */
  async upload(file: File): Promise<UploadedBlob> {
    const accountId = await this.account();
    const limit = await this.maxUploadBytes();
    if (file.size > limit) {
      throw new JmapError(
        `"${file.name}" is ${formatBytes(file.size)}. The limit is ${formatBytes(limit)}.`,
      );
    }
    const res = await this.http(`/jmap/upload/${encodeURIComponent(accountId)}/`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${this.token}`,
        // Deliberately the file's own type, not application/json: the body is
        // raw bytes and the server records this as the blob's content type.
        "content-type": file.type || "application/octet-stream",
      },
      body: file,
    });
    if (!res.ok) {
      throw new JmapError(
        res.status === 413
          ? `"${file.name}" is too large to attach.`
          : `Could not attach "${file.name}" (${res.status})`,
        res.status,
      );
    }
    return (await res.json()) as UploadedBlob;
  }

  /**
   * Fetches an attachment's bytes.
   *
   * A plain `<a href>` cannot be used: the download endpoint needs the bearer
   * token, and the only ways to put one on a navigation are a cookie or a token
   * in the URL — the first we do not have, the second would write the
   * credential into history and any referrer. So it is fetched and handed to
   * the page as an object URL.
   */
  async download(attachment: Attachment): Promise<Blob> {
    const accountId = await this.account();
    const name = attachment.name ?? "attachment";
    const url =
      `/jmap/download/${encodeURIComponent(accountId)}/${encodeURIComponent(attachment.blobId)}/` +
      `${encodeURIComponent(name)}?accept=${encodeURIComponent(attachment.type)}`;
    const res = await this.http(url, { headers: { authorization: `Bearer ${this.token}` } });
    if (!res.ok) throw new JmapError(`Could not download "${name}" (${res.status})`, res.status);
    return res.blob();
  }

  /** Permanently removes messages — used to clear a stale draft. */
  async destroy(ids: string[]): Promise<void> {
    const accountId = await this.account();
    await this.request([["Email/set", { accountId, destroy: ids }, "0"]]);
  }

  /**
   * Moves messages into exactly one mailbox.
   *
   * Every id goes in a single Email/set. Archiving a screenful one request at a
   * time would be forty round trips and forty chances to half-apply; batching
   * makes it one call the server applies together.
   */
  async move(ids: string[], toMailboxId: string): Promise<void> {
    if (ids.length === 0) return;
    const accountId = await this.account();
    const update = Object.fromEntries(
      ids.map((id) => [id, { mailboxIds: { [toMailboxId]: true } }]),
    );
    await this.request([["Email/set", { accountId, update }, "0"]]);
  }

  /** Adds or removes a keyword across many messages in one request. */
  async setKeywords(ids: string[], keyword: string, on: boolean): Promise<void> {
    if (ids.length === 0) return;
    const accountId = await this.account();
    const update = Object.fromEntries(
      ids.map((id) => [id, { [`keywords/${keyword}`]: on ? true : null }]),
    );
    await this.request([["Email/set", { accountId, update }, "0"]]);
  }
}

/**
 * The parts a message references from its own HTML by Content-ID.
 *
 * These are how mail embeds images: a signature logo, an inline screenshot, or
 * a newsletter's whole layout arrives as `<img src="cid:...">` plus a matching
 * part. They are NOT the paperclip list — showing them there would present a
 * signature logo as a file to download on half the mail people receive.
 */
export function inlineParts(email: Email): Attachment[] {
  return (email.attachments ?? []).filter((a) => !!a.cid);
}

/** Human-readable byte count — "1.4 MB", not "1468006 bytes". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["kB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * Picks the body to display: real HTML if the message has any, else plain text.
 *
 * `htmlBody` being non-empty does NOT mean the message is HTML. RFC 8621 has
 * htmlBody fall back to the text part when there is no HTML version, and
 * Stalwart does exactly that — a text-only message comes back with the *same*
 * part in both lists, same partId, `type: "text/plain"`. Keying off presence
 * alone therefore rendered every plain-text message inside the white HTML frame
 * instead of as text in the dark reading pane. The part's own type is the only
 * honest signal.
 */
export function bodyOf(email: Email): { html: string | null; text: string | null } {
  const values = email.bodyValues ?? {};
  const htmlPart = email.htmlBody?.[0];
  const textPart = email.textBody?.[0]?.partId;
  const isRealHtml = htmlPart?.type === "text/html";
  return {
    html: isRealHtml ? (values[htmlPart.partId]?.value ?? null) : null,
    text: textPart ? (values[textPart]?.value ?? null) : null,
  };
}
